import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Loader2, Check, Video, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

/**
 * RendyMappingSection
 * -------------------
 * Admin → Integrations → Rendy Fields. Mirrors RightmoveMappingSection:
 *   - Left: Rendy field name + type badge + required badge + description
 *   - Right: manual value OR a dropdown of our Property/CMS fields.
 * Auto-saves each row change to /api/admin/integrations/rendy-mapping.
 */
export default function RendyMappingSection() {
  const [rendyFields, setRendyFields] = useState([]);
  const [cmsFields, setCmsFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKeys, setSavingKeys] = useState({});
  const [savedKeys, setSavedKeys] = useState({});
  const [filter, setFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const savedTimeouts = useRef({});
  const valueDebounces = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/integrations/rendy-mapping`,
          { withCredentials: true }
        );
        setRendyFields(data.rendy_fields || []);
        setCmsFields(data.cms_fields || []);
        setMapping(data.mapping || {});
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.detail || "Failed to load mapping");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = useMemo(() => {
    const s = new Set(rendyFields.map((f) => f.group || "Other"));
    return ["all", ...Array.from(s)];
  }, [rendyFields]);

  const visibleFields = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rendyFields.filter((f) => {
      if (groupFilter !== "all" && (f.group || "Other") !== groupFilter) return false;
      if (!q) return true;
      const entry = mapping[f.name] || {};
      return (
        f.name.toLowerCase().includes(q) ||
        (f.description || "").toLowerCase().includes(q) ||
        (entry.cms_field || "").toLowerCase().includes(q) ||
        (entry.value || "").toLowerCase().includes(q)
      );
    });
  }, [rendyFields, filter, groupFilter, mapping]);

  const totalMapped = Object.values(mapping).filter(
    (e) => (e?.value || "").trim() || (e?.cms_field || "").trim()
  ).length;

  const persistPatch = async (fieldName, patch) => {
    setSavingKeys((s) => ({ ...s, [fieldName]: true }));
    try {
      const { data } = await axios.put(
        `${API}/admin/integrations/rendy-mapping`,
        { field: fieldName, ...patch },
        { withCredentials: true }
      );
      const serverEntry = data?.mapping?.[fieldName] ?? { value: "", cms_field: "" };
      setMapping((m) => ({ ...m, [fieldName]: serverEntry }));
      setSavedKeys((s) => ({ ...s, [fieldName]: true }));
      clearTimeout(savedTimeouts.current[fieldName]);
      savedTimeouts.current[fieldName] = setTimeout(() => {
        setSavedKeys((s) => {
          const { [fieldName]: _drop, ...rest } = s;
          return rest;
        });
      }, 1500);
    } catch (e) {
      console.error("Rendy mapping save failed:", e);
    } finally {
      setSavingKeys((s) => {
        const { [fieldName]: _drop, ...rest } = s;
        return rest;
      });
    }
  };

  const handleCmsChange = (fieldName, cmsKey) => {
    setMapping((m) => ({
      ...m,
      [fieldName]: { ...(m[fieldName] || { value: "" }), cms_field: cmsKey },
    }));
    persistPatch(fieldName, { cms_field: cmsKey });
  };

  const handleValueChange = (fieldName, newValue) => {
    setMapping((m) => ({
      ...m,
      [fieldName]: { ...(m[fieldName] || { cms_field: "" }), value: newValue },
    }));
    clearTimeout(valueDebounces.current[fieldName]);
    valueDebounces.current[fieldName] = setTimeout(() => {
      persistPatch(fieldName, { value: newValue });
    }, 600);
  };

  const anySaving = Object.keys(savingKeys).length > 0;
  const anySaved = !anySaving && Object.keys(savedKeys).length > 0;
  const autosaveLabel = anySaving ? "Saving…" : anySaved ? "Saved" : "Autosave on";

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Rendy mapping…
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-sm">{error}</div>;
  }

  return (
    <div className="space-y-6" data-testid="rendy-mapping-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" /> Rendy Fields
            <span
              className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-muted-foreground"
              data-testid="rendy-autosave-status"
            >
              {anySaving && <><RefreshCw className="h-3 w-3 animate-spin" /> {autosaveLabel}</>}
              {anySaved && <><Check className="h-3 w-3 text-emerald-600" /> {autosaveLabel}</>}
              {!anySaving && !anySaved && <span className="opacity-60">{autosaveLabel}</span>}
            </span>
          </CardTitle>
          <CardDescription>
            For each Rendy field, enter a <span className="font-medium text-foreground">manual value</span> to hard-code the output <em>or</em> pick a <span className="font-medium text-foreground">CMS field</span> from the dropdown to source it per property. If a manual value is set it always wins.
            Each change auto-saves. Currently mapped: <span className="font-medium text-foreground">{totalMapped}</span> / {rendyFields.length} fields.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium block mb-1">Search</label>
              <Input
                placeholder="field name, description, or CMS key…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="rendy-mapping-search"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Group</label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="rendy-mapping-group"
              >
                {groups.map((g) => (
                  <option key={g} value={g}>{g === "all" ? "All groups" : g}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border rounded-md overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr] bg-muted/40 border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <div>Rendy field</div>
              <div>Manual value  <span className="text-muted-foreground/60 normal-case">OR</span>  CMS field</div>
            </div>
            {visibleFields.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">No fields match your filters.</div>
            )}
            {visibleFields.map((f) => {
              const entry = mapping[f.name] || { value: "", cms_field: "" };
              const cur = entry.cms_field || "";
              const valueStr = entry.value || "";
              const usingValue = valueStr.trim().length > 0;
              const usingCms = !usingValue && cur.trim().length > 0;
              const saving = !!savingKeys[f.name];
              const saved = !!savedKeys[f.name];
              return (
                <div
                  key={f.name}
                  className="grid grid-cols-[1.4fr_1fr] px-4 py-3 border-b last:border-b-0 gap-4 items-start hover:bg-muted/20"
                  data-testid={`rendy-row-${f.name}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-sm font-semibold bg-muted px-1.5 py-0.5 rounded">{f.name}</code>
                      <Badge variant="outline" className="text-[10px]">{f.type}</Badge>
                      {f.required && (
                        <Badge className="text-[10px] bg-red-100 text-red-800 border-red-300">Required</Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px]">{f.group}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {f.description}
                    </p>
                    {Array.isArray(f.allowed_values) && f.allowed_values.length > 0 && (
                      <p className="text-[11px] text-muted-foreground/80 mt-1">
                        <span className="font-medium">Allowed:</span> {f.allowed_values.join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="min-w-0 space-y-2">
                    {!usingCms && (
                      <div className="flex items-center gap-2 min-w-0" data-testid={`rendy-manual-row-${f.name}`}>
                        {Array.isArray(f.allowed_values) && f.allowed_values.length > 0 ? (
                          <select
                            value={valueStr}
                            onChange={(e) => handleValueChange(f.name, e.target.value)}
                            className={`flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm min-w-0 ${usingValue ? "border-green-400 bg-green-50" : ""}`}
                            data-testid={`rendy-value-${f.name}`}
                          >
                            <option value="">— Not set —</option>
                            {f.allowed_values.map((av) => (
                              <option key={av} value={av}>{av}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            placeholder="Manual value (overrides field)"
                            value={valueStr}
                            onChange={(e) => handleValueChange(f.name, e.target.value)}
                            className={`h-9 text-sm ${usingValue ? "border-green-400 bg-green-50" : ""}`}
                            data-testid={`rendy-value-${f.name}`}
                          />
                        )}
                        {usingValue && (
                          <Badge className="text-[10px] bg-green-100 text-green-800 border-green-300 whitespace-nowrap">
                            value wins
                          </Badge>
                        )}
                        {usingValue && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                            {!saving && saved && <Check className="h-4 w-4 text-green-600" />}
                          </div>
                        )}
                      </div>
                    )}
                    {!usingValue && (
                      <div className="flex items-center gap-2 min-w-0">
                        <select
                          value={cur}
                          onChange={(e) => handleCmsChange(f.name, e.target.value)}
                          className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm min-w-0"
                          data-testid={`rendy-select-${f.name}`}
                        >
                          <option value="">— unmapped —</option>
                          {cmsFields.map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.label} ({c.type})
                            </option>
                          ))}
                        </select>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                          {!saving && saved && <Check className="h-4 w-4 text-green-600" />}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
